import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import type { Permission } from '@/constants/permissions';
import { canFor } from '@/composables/usePermissions';
import { globalErrors } from '@/composables/useErrors';
import { useOrganisationStore } from '@/stores/organisationStore';

/**
 * Where to send someone who has been refused.
 *
 * Back to the organisation they were browsing, if they are actually in it --
 * being short one permission is no reason to move them. If they are not, that
 * URL is a stale bookmark for an organisation they have left, and its dashboard
 * would render another organisation's data; use one they still belong to.
 */
function denialDestination(to: RouteLocationNormalized): string {
    const store = useOrganisationStore();
    const wanted = to.params['organisation'];

    if (typeof wanted === 'string' && store.findOrganisationByPublicId(wanted)) {
        return wanted;
    }

    return store.current_organisation.public_id;
}

/**
 * Keeps users out of routes their role does not allow, declared per route as
 * `meta: { permission: '…' }`.
 *
 * Registered after the auth guard, which has already awaited the profile -- so
 * the permission list is loaded and a denial here is real rather than a race.
 *
 * The check asks about the organisation in the route being navigated *to*, not
 * the one the store currently holds: on a cold load into a deep link the store
 * still has whatever organisation was last in localStorage.
 *
 * Users mostly arrive at a gated route from a stale bookmark or a typed URL,
 * since the menu hides what they cannot reach, so the dashboard is the least
 * alarming place to land. The toast stops the redirect reading as a bug.
 */
export function permissionGuard(to: RouteLocationNormalized): true | RouteLocationRaw {
    const required = to.meta['permission'] as Permission | undefined;

    if (!required || canFor(to.params['organisation'] as string, required)) {
        return true;
    }

    console.info(`Missing permission "${required}", redirecting to dashboard...`);
    globalErrors()?.showWarning('You do not have access to that page.', 'You do not have permission');

    return { name: 'dashboard', params: { organisation: denialDestination(to) } };
}
